/**
 * SMS lists + members + drip steps API (migrations 0116/0117).
 * Companion to api/sms.ts (messages/DNC) and api/smsCampaigns.ts
 * (broadcasts/keywords).
 */
import { apiFetch } from './client';

export interface SmsListRow {
  id: string;
  reseller_id: string;
  customer_id: string;
  /** Per-customer sequential ref, displayed as SML-N via formatRef. */
  ref_number: number;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  /** Live subscribed-member count (list endpoints). */
  member_count?: number;
  /** Active drip-step count (list endpoints). */
  drip_step_count?: number;
}

export interface SmsListMemberRow {
  id: string;
  reseller_id: string;
  customer_id: string;
  list_id: string;
  e164: string;
  display_name: string | null;
  caller_id: string | null;
  subscribed_at: string;
  unsubscribed_at: string | null;
  source: 'portal' | 'keyword' | 'import';
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface SmsDripStepRow {
  id: string;
  reseller_id: string;
  customer_id: string;
  list_id: string;
  step_order: number;
  offset_hours: number;
  body: string;
  active: boolean;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface ImportResult {
  inserted: number;
  resubscribed: number;
  existing: number;
  failed: Array<{ line: number; raw: string; error: string }>;
}

export const smsLists = {
  list: (p: { customer_id?: string; search?: string; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (p.customer_id) q.set('customer_id', p.customer_id);
    if (p.search) q.set('search', p.search);
    if (p.limit) q.set('limit', String(p.limit));
    return apiFetch<{ rows: SmsListRow[] }>(`/api/sms-lists?${q}`);
  },
  get: (id: string) => apiFetch<SmsListRow>(`/api/sms-lists/${id}`),
  create: (body: { name: string; description?: string | null; customer_id?: string }) =>
    apiFetch<SmsListRow>('/api/sms-lists', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: string, body: { name?: string; description?: string | null }) =>
    apiFetch<SmsListRow>(`/api/sms-lists/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  remove: (id: string) =>
    apiFetch<void>(`/api/sms-lists/${id}`, { method: 'DELETE' }),

  listMembers: (listId: string, p: { search?: string; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (p.search) q.set('search', p.search);
    if (p.limit) q.set('limit', String(p.limit));
    return apiFetch<{ rows: SmsListMemberRow[] }>(`/api/sms-lists/${listId}/members?${q}`);
  },
  addMember: (listId: string, body: { e164: string; display_name?: string | null }) =>
    apiFetch<{ member: SmsListMemberRow; outcome: 'inserted' | 'resubscribed' | 'existing' }>(
      `/api/sms-lists/${listId}/members`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  importMembers: (listId: string, text: string) =>
    apiFetch<ImportResult>(`/api/sms-lists/${listId}/members/import`, {
      method: 'POST', body: JSON.stringify({ text }),
    }),
  removeMember: (memberId: string) =>
    apiFetch<void>(`/api/sms-list-members/${memberId}`, { method: 'DELETE' }),

  listDripSteps: (listId: string) =>
    apiFetch<{ rows: SmsDripStepRow[] }>(`/api/sms-lists/${listId}/drip-steps`),
  createDripStep: (listId: string, body: {
    offset_hours: number; body: string; step_order?: number; active?: boolean;
  }) =>
    apiFetch<SmsDripStepRow>(`/api/sms-lists/${listId}/drip-steps`, {
      method: 'POST', body: JSON.stringify(body),
    }),
  updateDripStep: (stepId: string, body: {
    offset_hours?: number; body?: string; step_order?: number; active?: boolean;
  }) =>
    apiFetch<SmsDripStepRow>(`/api/sms-drip-steps/${stepId}`, {
      method: 'PATCH', body: JSON.stringify(body),
    }),
  removeDripStep: (stepId: string) =>
    apiFetch<void>(`/api/sms-drip-steps/${stepId}`, { method: 'DELETE' }),
};
